import type { URLSearchParams } from "url";
import { ptBR } from "date-fns/locale";
import { StatusCredential } from "libLegacy/api/auth";
import { AlertProps } from "@mui/material";
import { MESSAGES } from "texts/BillingCredentialAlerts";

export type ParsedDateMyBills = {
  /* Month in DD format  */
  month: string;
  /* Year in YYYY format */
  year: string;
  referenceDate: Date;
};

export const parseDateFromUrl = (
  todayTimedDate: Date,
  urlParams: URLSearchParams
): ParsedDateMyBills => {
  const month = urlParams.get("month") || "";
  const year = urlParams.get("year") || "";

  todayTimedDate.setFullYear(+year);
  todayTimedDate.setMonth(+month - 1, 1); // turns human months (1 - 12) into js months (0 - 11)

  return { month, year, referenceDate: todayTimedDate };
};

export const parseDateFromToday = (todayTimedDate: Date): ParsedDateMyBills => {
  todayTimedDate.setMonth(todayTimedDate.getMonth() - 1, 1);

  const month = todayTimedDate.toLocaleDateString(ptBR.code, {
    month: "2-digit",
  });
  const year = todayTimedDate.toLocaleDateString(ptBR.code, {
    year: "numeric",
  });

  return { month, year, referenceDate: todayTimedDate };
};

export const parseDateFromPicker = (
  pickerDate: Date,
  today: Date,
  isYearly: boolean
): Omit<ParsedDateMyBills, "referenceDate"> => {
  // if on monthly view, just get the day and month from the date obj
  let month = pickerDate.toLocaleDateString(ptBR.code, {
    month: "2-digit",
  });
  let year = pickerDate.toLocaleDateString(ptBR.code, {
    year: "numeric",
  });

  if (isYearly) {
    month = "12";
  }

  return { month, year };
};

export type CredentialAlertBilling = {
  severity: NonNullable<AlertProps["severity"]>;
  message: string;
  requiresCredentialUpdate: boolean;
  actionText?: string;
};

export const mapCredentialStatusToAlertProps = (
  status: Exclude<StatusCredential, "VALIDATED">
): CredentialAlertBilling => {
  const message = MESSAGES[status];
  switch (status) {
    case "MISSING":
      return {
        actionText: "Preencher",
        severity: "info",
        message,
        requiresCredentialUpdate: true,
      };
    case "PENDING": // intentional fall through: they have the same outcome
    case "UNSUPPORTED":
      return { severity: "info", message, requiresCredentialUpdate: false };
    case "INVALIDATED":
      return {
        actionText: "Repreencher",
        severity: "error",
        message,
        requiresCredentialUpdate: true,
      };
    default:
      throw new Error(
        "Invalid credential status supplied to mapCredentialStatusToAlertProps"
      );
  }
};

/** Uses backendFilter's month/year pair to create a to value (last day from the same month
 *  and year) and a from value (first day of the same month, a year before) that are 13
 * months apart to be used in the chart fetch call
 */
export const formatChartFetchParams = (
  month: string,
  year: string
): { from: Date; to: Date } => {
  const to = new Date(+year, +month, 0);
  const from = new Date(+year, +month - 12, 1);

  return { from, to };
};
